import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Form, Select } from 'antd';
import axiosApiInstance from '../../../api/axiosClient';
import { useCategoriesManualQuery } from './queryHooks';

export const CreatorLinkInfoAlert: React.FC = () => (
    <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Link creators when this connection represents a creator"
        description="If this connection belongs to a creator, search and link them below and set the category to Creator. This keeps creator profiles and their contact records in sync."
    />
);

type Option = {
    label: string;
    value: number;
    creator: API.Creators.Creator;
};

type Props = {
    value: number[];
    onChange: (ids: number[]) => void;
};

function formatCreatorLabel(creator: API.Creators.Creator): string {
    const name = creator.creator?.trim() || '(unnamed)';
    return creator.id ? `${name} (#${creator.id})` : name;
}

const creatorSearchParams = (search: string) => ({
    search,
    page: 1,
    page_size: 50,
    youtube_followers_min: 0,
    youtube_followers_max: 1000000,
    instagram_followers_min: 0,
    instagram_followers_max: 1000000,
    tiktok_followers_min: 0,
    tiktok_followers_max: 1000000,
});

/**
 * CreatorLinkField — connection↔creator link control for the new-connection form.
 * Selection is controlled via value/onChange props (not form fields) so submit
 * always includes the chosen creator IDs.
 */
const CreatorLinkField: React.FC<Props> = ({ value, onChange }) => {
    const form = Form.useFormInstance<API.CRM.Contact>();
    const { refetch: fetchCategories } = useCategoriesManualQuery();
    const [search, setSearch] = useState<string>('');
    const [options, setOptions] = useState<Option[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const creatorsByIdRef = useRef<Record<number, API.Creators.Creator>>({});

    useEffect(() => {
        const handle = setTimeout(() => {
            setLoading(true);
            axiosApiInstance
                .get<API.DataResponse<API.Creators.Creator[]>>('/api/creators/items', {
                    params: creatorSearchParams(search),
                })
                .then(({ data }) => {
                    const list = (data?.data ?? []).slice(0, 50);
                    setOptions(
                        list.map((c) => ({
                            label: formatCreatorLabel(c),
                            value: c.id!,
                            creator: c,
                        }))
                    );
                    for (const c of list) {
                        if (c.id != null) creatorsByIdRef.current[c.id] = c;
                    }
                })
                .catch((err) => console.error('creator search failed', err))
                .finally(() => setLoading(false));
        }, 300);
        return () => clearTimeout(handle);
    }, [search]);

    useEffect(() => {
        if (!value.length) return;

        fetchCategories().then(({ data: categories }) => {
            const creatorCategory = (categories ?? []).find(
                (cat) => cat.name?.trim().toLowerCase() === 'creator'
            );
            if (!creatorCategory) return;

            const currentCategory = form.getFieldValue('contact_category_id');
            if (!currentCategory) {
                form.setFieldValue('contact_category_id', creatorCategory.id);
            }
        });
    }, [value, form, fetchCategories]);

    const allOptions = useMemo<Option[]>(() => {
        const seen = new Set(options.map((o) => o.value));
        const extra: Option[] = [];
        for (const id of value) {
            if (!seen.has(id)) {
                const creator = creatorsByIdRef.current[id];
                extra.push({
                    label: creator ? formatCreatorLabel(creator) : `Creator #${id}`,
                    value: id,
                    creator: creator!,
                });
            }
        }
        return [...options, ...extra];
    }, [options, value]);

    return (
        <Form.Item label="Linked Creators" style={{ marginBottom: 16, maxWidth: 600 }}>
            <Select<number[]>
                mode="multiple"
                showSearch
                allowClear
                value={value}
                placeholder="Search and add creators by name…"
                filterOption={false}
                loading={loading}
                onSearch={setSearch}
                onChange={onChange}
                options={allOptions}
                notFoundContent={loading ? 'Searching…' : 'No creators found'}
                style={{ width: '100%' }}
            />
        </Form.Item>
    );
};

export default CreatorLinkField;
